export const USER_ROLES = ["Client Admin", "Client Viewer"];

/** Tenant roles allowed to see Client Administration (nav + Import page). */
export const CLIENT_ADMINISTRATION_ROLES = ["Admin", "Client Admin"];

/** Same roles: bulk/row delete, master address add/delete, and other tenant mutations. */
export function canMutateTenantData(userRole) {
  return CLIENT_ADMINISTRATION_ROLES.includes(userRole);
}

export function canAccessClientAdministration(userRole) {
  return canMutateTenantData(userRole);
}

/** Route ids for Client Administration (nav dropdown + ImportPage). */
export const CLIENT_ADMIN_PAGE = {
  IMPORT: "client-admin-import",
  INVOICE: "client-admin-invoice",
  ACTIVITY: "client-admin-activity",
};

export const CLIENT_ADMIN_PAGES = [
  CLIENT_ADMIN_PAGE.IMPORT,
  CLIENT_ADMIN_PAGE.INVOICE,
  CLIENT_ADMIN_PAGE.ACTIVITY,
];

export function isClientAdminPage(page) {
  return CLIENT_ADMIN_PAGES.includes(page);
}

/** Session keys: add missing invoice from Invoices dashboard → upload → return. */
export const INVOICE_UPLOAD_PREFILL_KEY = "invoiceUploadPrefill";
export const INVOICE_UPLOAD_RETURN_KEY = "invoiceUploadReturn";
export const INVOICE_UPLOAD_SUCCESS_TOAST_KEY = "invoiceUploadSuccessToast";
export const INVOICE_ACCOUNT_SEARCH_KEY = "invoiceAccountSearch";
/** Session key: Invoices → Detailed Inventory search by billing account. */
export const INVENTORY_ACCOUNT_SEARCH_KEY = "inventoryAccountSearch";

/** Session keys: Overview → target page filters / tabs. */
export const OVERVIEW_INVOICE_STATUS_KEY = "overviewInvoiceStatusFilter";
export const OVERVIEW_MASTER_ADDRESS_TAB_KEY = "overviewMasterAddressTab";
export const OVERVIEW_INVENTORY_FILTERS_KEY = "overviewInventoryFilters";

/** Session keys: header global search → target page. */
export const MASTER_ADDRESS_SEARCH_KEY = "masterAddressSearch";
export const GLOBAL_SEARCH_INVOICE_HANDOFF_KEY = "globalSearchInvoiceHandoff";

// NOTE: These constants are used as fallback values when the API fails or during loading.
// The app now uses dynamic values fetched from the database via useDropdownValues hook.
// These are kept for backward compatibility and error handling.
export const PROVIDERS = [
  "Nitel",
  "MetTel",
  "Lumen",
  "GoTo",
  "Custom",
  "AT&T",
  "Spectrotel",
];

export const CATEGORIES = ["Analog", "Custom", "Data", "Data+Voice", "Mobility", "Voice"];

// Provider-Category mapping: defines valid category combinations for each provider
// AT&T and Spectrotel use "Data+Voice" as a combined category because a single report
// can mix services. Per-record category is set during import (transformATTData for AT&T,
// transformSpectrotelData for Spectrotel).
export const PROVIDER_CATEGORY_MAPPING = {
  Nitel: ["Data"],
  MetTel: ["Analog", "Mobility"],
  Lumen: ["Data"],
  GoTo: ["Voice"],
  Custom: ["Custom"],
  "AT&T": ["Data+Voice"],
  Spectrotel: ["Data+Voice"],
};

// Category-Provider mapping: defines valid providers for each category (reverse mapping)
// "Data+Voice" is a combined category for AT&T (Service Family) and Spectrotel (Product).
export const CATEGORY_PROVIDER_MAPPING = {
  Data: ["Lumen", "Nitel"],
  Analog: ["MetTel"],
  Mobility: ["MetTel"],
  Voice: ["GoTo"],
  Custom: ["Custom"],
  // "Data+Voice": ["AT&T"],
  "Data+Voice": ["AT&T", "Spectrotel"],
};

// File upload constants
export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// NOTE: SERVICE_TYPES is used as fallback when API fails or during loading.
// The app now uses dynamic service type values fetched from the database.
export const SERVICE_TYPES = [
  "Dedicated Internet Access",
  "Enterprise Wireless Access Service",
  "Network Service Management",
  "Private Line",
  "SD-WAN",
  "Voice",
  "Wireless",
];

// NOTE: STATUS_OPTIONS is used as fallback when API fails or during loading.
// The app now uses dynamic status values fetched from the database.
export const STATUS_OPTIONS = [
  "In Service",
  "Pending",
  "Pending Install",
  "Pending Disconnect",
  "Disconnected",
  "Canceled",
  "Inactive",
  "Suspended",
];

// NOTE: These tenant IDs now match the database IDs from initPostgres.js
// The actual tenant ID is retrieved from the authenticated user's JWT token
export const TENANTS = {
  circuitflow: {
    id: 1, // AssetLedger tenant ID from database
    name: "AssetLedger",
    logo: 'data:image/svg+xml;utf8,<svg width="180" height="70" viewBox="0 0 180 70" xmlns="http://www.w3.org/2000/svg"><g transform="translate(75 10) scale(1.4)"><path fill="%2360A5FA" d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7zM9 21a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-1H9v1z"/></g><text x="90" y="55" font-family="IBM Plex Sans, Segoe UI, Helvetica, Arial, sans-serif" font-size="18" font-weight="700" fill="%231F2937" text-anchor="middle" transform="translate(0 5)">AssetLedger</text></svg>',
    primaryColor: "blue-500",
    primaryBgColor: "bg-blue-500",
    primaryTextColor: "text-blue-500",
  },
  "quantum-solutions": {
    id: 2, // Quantum Solutions tenant ID from database
    name: "Quantum Solutions",
    logo: "https://placehold.co/150x50/16a34a/ffffff?text=Quantum+Sol.&font-inter",
    primaryColor: "green-600",
    primaryBgColor: "bg-green-600",
    primaryTextColor: "text-green-600",
  },
  "tech-corp": {
    id: 3, // Tech Corp tenant ID from database
    name: "Tech Corp",
    logo: "https://placehold.co/150x50/7c3aed/ffffff?text=Tech+Corp&font-inter",
    primaryColor: "purple-600",
    primaryBgColor: "bg-purple-600",
    primaryTextColor: "text-purple-600",
  },
  "global-solutions": {
    id: 4, // Global Solutions tenant ID from database
    name: "Global Solutions",
    logo: "https://placehold.co/150x50/ea580c/ffffff?text=Global+Sol&font-inter",
    primaryColor: "orange-600",
    primaryBgColor: "bg-orange-600",
    primaryTextColor: "text-orange-600",
  },
};

export const MOCK_INVENTORY = [
  {
    id: 1,
    circuit_id: "AZ-123-456",
    provider: "Nitel",
    category: "Data",
    service_type: "Fiber",
    status: "Active",
    address: "123 Main St, New York, NY",
    mrc: 150.0,
    end_of_term: "2025-12-31",
    bandwidth: "1 Gbps",
    last_mile_provider: "Verizon",
    provider_circuit_id: "VZB-987654",
    billing_account: "987654321",
    notes: "Standard install.",
    follow_up_date: "2025-10-01",
    primary_contact_name: "Alice Johnson",
    primary_contact_phone: "212-555-0101",
    primary_contact_email: "alice.j@example.com",
    secondary_contact_name: "Bob Williams",
    secondary_contact_phone: "212-555-0102",
    secondary_contact_email: "bob.w@example.com",
  },
  {
    id: 2,
    circuit_id: "CA-987-654",
    provider: "MetTel POTS",
    category: "Analog",
    service_type: "POTS",
    status: "Active",
    address: "456 Oak Ave, Los Angeles, CA",
    mrc: 45.5,
    end_of_term: "2026-06-30",
    bandwidth: "N/A",
    last_mile_provider: "AT&T",
    provider_circuit_id: "ATT-123456",
    billing_account: "123456789",
    notes: "",
    follow_up_date: "2026-04-01",
    primary_contact_name: "Carlos Ray",
    primary_contact_phone: "310-555-0103",
    primary_contact_email: "carlos.r@example.com",
    secondary_contact_name: "Diana Prince",
    secondary_contact_phone: "310-555-0104",
    secondary_contact_email: "diana.p@example.com",
  },
  {
    id: 3,
    circuit_id: "TX-456-789",
    provider: "MetTel Mobility",
    category: "Mobility",
    service_type: "5G",
    status: "Pending Install",
    address: "789 Pine Ln, Houston, TX",
    mrc: 99.99,
    end_of_term: "2027-01-15",
    bandwidth: "5G Unlimited",
    last_mile_provider: "T-Mobile",
    provider_circuit_id: "MM-A1B2C3",
    billing_account: "A1B2C3D4E5",
    notes: "New device for exec.",
    follow_up_date: "2026-11-01",
    first_name: "Jim",
    last_name: "Brown",
    email: "jim.brown@example.com",
    primary_contact_name: "Eva Green",
    primary_contact_phone: "713-555-0105",
    primary_contact_email: "eva.g@example.com",
    secondary_contact_name: "Frank White",
    secondary_contact_phone: "713-555-0106",
    secondary_contact_email: "frank.w@example.com",
  },
  {
    id: 4,
    circuit_id: "FL-789-123",
    provider: "Lumen",
    category: "SD Wan",
    service_type: "MPLS",
    status: "Disconnected",
    address: "101 Palm Way, Miami, FL",
    mrc: 450.75,
    end_of_term: "2024-11-20",
    bandwidth: "50 Mbps",
    last_mile_provider: "Lumen",
    provider_circuit_id: "LMN-FL-001",
    billing_account: "LMN987",
    notes: "Decommissioned.",
    follow_up_date: "",
    primary_contact_name: "Grace Hall",
    primary_contact_phone: "305-555-0107",
    primary_contact_email: "grace.h@example.com",
    secondary_contact_name: "Henry Ivan",
    secondary_contact_phone: "305-555-0108",
    secondary_contact_email: "henry.i@example.com",
  },
  {
    id: 5,
    circuit_id: "IL-234-567",
    provider: "Nitel",
    category: "Data",
    service_type: "Fiber",
    status: "Active",
    address: "212 River St, Chicago, IL",
    mrc: 1150.0,
    end_of_term: "2026-08-01",
    bandwidth: "10 Gbps",
    last_mile_provider: "Comcast",
    provider_circuit_id: "NIT-CHI-005",
    billing_account: "987654321",
    notes: "VIP location.",
    follow_up_date: "2026-06-01",
    primary_contact_name: "Ivy Jones",
    primary_contact_phone: "312-555-0109",
    primary_contact_email: "ivy.j@example.com",
    secondary_contact_name: "Jack King",
    secondary_contact_phone: "312-555-0110",
    secondary_contact_email: "jack.k@example.com",
  },
  {
    id: 6,
    circuit_id: "WA-876-543",
    provider: "Lumen",
    category: "Data",
    service_type: "Fiber",
    status: "Active",
    address: "333 Pike Pl, Seattle, WA",
    mrc: 180.25,
    end_of_term: "2025-10-10",
    bandwidth: "100 Mbps",
    last_mile_provider: "CenturyLink",
    provider_circuit_id: "CTL-SEA-456",
    billing_account: "CTL123",
    notes: "",
    follow_up_date: "2025-08-01",
    primary_contact_name: "Kara Lane",
    primary_contact_phone: "206-555-0111",
    primary_contact_email: "kara.l@example.com",
    secondary_contact_name: "Leo Martin",
    secondary_contact_phone: "206-555-0112",
    secondary_contact_email: "leo.m@example.com",
  },
  {
    id: 7,
    circuit_id: "GA-321-654",
    provider: "MetTel POTS",
    category: "Analog",
    service_type: "POTS",
    status: "Pending Install",
    address: "444 Peachtree Rd, Atlanta, GA",
    mrc: 55.0,
    end_of_term: "2027-05-05",
    bandwidth: "N/A",
    last_mile_provider: "AT&T",
    provider_circuit_id: "ATT-GA-789",
    billing_account: "987-GA",
    notes: "",
    follow_up_date: "2027-03-01",
    primary_contact_name: "Mona Nelson",
    primary_contact_phone: "404-555-0113",
    primary_contact_email: "mona.n@example.com",
    secondary_contact_name: "Oscar Price",
    secondary_contact_phone: "404-555-0114",
    secondary_contact_email: "oscar.p@example.com",
  },
  {
    id: 8,
    circuit_id: "OH-555-444",
    provider: "Nitel",
    category: "Data",
    service_type: "Fiber",
    status: "Active",
    address: "555 Cleveland Ave, Columbus, OH",
    mrc: 135.0,
    end_of_term: "2026-01-01",
    bandwidth: "500 Mbps",
    last_mile_provider: "Spectrum",
    provider_circuit_id: "NIT-OH-123",
    billing_account: "SPEC-OH-1",
    notes: "",
    follow_up_date: "2025-11-01",
    primary_contact_name: "Paul Queen",
    primary_contact_phone: "614-555-0115",
    primary_contact_email: "paul.q@example.com",
    secondary_contact_name: "Rita Stone",
    secondary_contact_phone: "614-555-0116",
    secondary_contact_email: "rita.s@example.com",
  },
  {
    id: 9,
    circuit_id: "CO-111-222",
    provider: "Lumen",
    category: "SD Wan",
    service_type: "MPLS",
    status: "Active",
    address: "111 Denver Rd, Denver, CO",
    mrc: 650.0,
    end_of_term: "2025-02-14",
    bandwidth: "100 Mbps",
    last_mile_provider: "Lumen",
    provider_circuit_id: "LMN-CO-321",
    billing_account: "LMN-CO-1",
    notes: "",
    follow_up_date: "2024-12-01",
    primary_contact_name: "Sam Tucker",
    primary_contact_phone: "303-555-0117",
    primary_contact_email: "sam.t@example.com",
    secondary_contact_name: "Tina Underwood",
    secondary_contact_phone: "303-555-0118",
    secondary_contact_email: "tina.u@example.com",
  },
  {
    id: 10,
    circuit_id: "MA-333-111",
    provider: "MetTel Mobility",
    category: "Mobility",
    service_type: "5G",
    status: "Active",
    address: "333 Boston St, Boston, MA",
    mrc: 110.0,
    end_of_term: "2027-08-20",
    bandwidth: "5G Unlimited+",
    last_mile_provider: "Verizon",
    provider_circuit_id: "MM-MA-001",
    billing_account: "VZW-MA-ACCT",
    notes: "",
    follow_up_date: "2027-06-01",
    first_name: "Robert",
    last_name: "Wilson",
    email: "robert.w@example.com",
    primary_contact_name: "Uma Vance",
    primary_contact_phone: "617-555-0119",
    primary_contact_email: "uma.v@example.com",
    secondary_contact_name: "Victor West",
    secondary_contact_phone: "617-555-0120",
    secondary_contact_email: "victor.w@example.com",
  },
];
